import React, { useRef, useEffect, useMemo } from "react";
import { attemptFullscreenToggle } from "../../../../utilities";
import { Sprite, Container, Text, Graphics } from "@pixi/react";
import * as PIXI from "pixi.js";
import { TextStyle, TextMetrics } from "pixi.js";
import anime from "animejs";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { openBonusBuy, closeMenu } from "../../../../State/actions";
import {
  selectMenuActive,
  selectCriticalAnimationPlaying,
  selectIsSpinning,
  selectAutoPlayActive,
  selectAnyFeatureActive,
  selectAnyFeatureTriggeredOrActive,
  selectFreeRoundsActive,
  selectReplaySessionActive,
} from "../../../../State/selectors";
import { SOUND_NAME } from "../../../../sounds";
import { getNewUITexture } from "../../../../assets";
import ButtonShadow from "./ButtonShadow";
// import { useButtonShadow } from "../../../../utils/buttonShadow";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import { FONT_CONFIG } from "../../../../pixiSettings";
import { useButtonBrightness } from "../../../../Hooks/useButtonBrightness";
import { triggerScalePress } from "../../../../Hooks/useButtonHover";

// Bonus buy button configuration - matches UI button dimensions
const BONUS_BUY_BUTTON_CONFIG = {
  BASE_WIDTH: 80,
  BASE_HEIGHT: 80,
  // An iPad upright (layout TABLET_PORTRAIT on the MOBILE device, 2026-10-29):
  // slightly bigger, as in the platform's own games — its places are in its
  // own size, so the gaps grow with it.
  LAYOUT_CONFIGS: {
    // TABLET_PORTRAIT: { deviceMultiplier: 2.14 },
    TABLET_PORTRAIT: { deviceMultiplier: 2.18, stackGap: 1.35 }, // a tiny bit larger; BONUS a bit higher
  },
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE_SMALL]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.03,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.575,
      },
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.04,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.5,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.3,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.7,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.0,
    },
  },
};

// ── Moved to utils/buttonShadow.js (one source for every round button) ──
// // ── A soft drop shadow under the button (2026-10-05: not enough contrast on
// // some backgrounds) ── the same kind the UI text wears
// // (TEXT_STYLE_CONFIG.OUTLINE: a canvas drop shadow, soft, offset down-right),
// // a little stronger so it reads on the orange. Sizes are shares of the
// // button's radius; drawn ONCE per size into a texture, nothing per frame.
// const BUTTON_SHADOW = {
//   ALPHA: 0.35,
//   BLUR: 0.14, // x radius
//   DISTANCE: 0.05, // x radius
//   ANGLE: Math.PI / 4, // down-right, as the text's
// };
//
// /** Only the SHADOW of a circle of `radius`, on a canvas, as a texture. */
// const makeButtonShadowTexture = (radius, resolution) => {
//   const blur = radius * BUTTON_SHADOW.BLUR;
//   const dist = radius * BUTTON_SHADOW.DISTANCE;
//   const pad = Math.ceil(blur * 2 + dist + 2);
//   const size = Math.ceil(radius * 2 + pad * 2);
//   const canvas = document.createElement("canvas");
//   canvas.width = Math.ceil(size * resolution);
//   canvas.height = Math.ceil(size * resolution);
//   const ctx = canvas.getContext("2d");
//   // The circle itself is drawn far off the canvas; only its shadow lands on
//   // it. shadowBlur / shadowOffset are in DEVICE pixels (not transformed).
//   const away = canvas.width * 4;
//   ctx.shadowColor = `rgba(0, 0, 0, ${BUTTON_SHADOW.ALPHA})`;
//   ctx.shadowBlur = blur * resolution;
//   ctx.shadowOffsetX = -away + Math.cos(BUTTON_SHADOW.ANGLE) * dist * resolution;
//   ctx.shadowOffsetY = Math.sin(BUTTON_SHADOW.ANGLE) * dist * resolution;
//   ctx.beginPath();
//   ctx.arc(canvas.width / 2 + away, canvas.height / 2, radius * resolution, 0, Math.PI * 2);
//   ctx.fill();
//   return PIXI.Texture.from(canvas, { resolution });
// };

const ButtonBonusBuy = ({ toolbar }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    getResponsiveConfig,
    device,
    orientation,
    dimensions,
    isMobileSmall,
  } = useDimensions();

  // Redux state
  const menuActive = useSelector(selectMenuActive);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const isSpinning = useSelector(selectIsSpinning);
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const anyFeatureActive = useSelector(selectAnyFeatureActive);
  const anyFeatureTriggeredOrActive = useSelector(
    selectAnyFeatureTriggeredOrActive,
  );
  const freeRoundsActive = useSelector(selectFreeRoundsActive);
  const replaySessionActive = useSelector(selectReplaySessionActive);

  // Refs
  const buttonSpriteRef = useRef(null);
  const buttonTextRef = useRef(null);
  const animationRef = useRef(null);
  const restoreTimeoutRef = useRef(null);
  const isProcessingClickRef = useRef(false);
  const debounceTimeoutRef = useRef(null);
  const dimensionsRef = useRef({ width: 0, height: 0 });
  const isInitializedRef = useRef(false);
  const previousMenuStateRef = useRef(null);
  const buttonContainerRef = useRef(null);

  // Hidden when any feature game is active
  const isHidden = anyFeatureActive;

  // Texture — from new spritesheet
  const texture = getNewUITexture("button_buy_bonus_background");

  // Check if we're on mobile landscape (only apply menu animation here)
  const isMobileLandscape = useMemo(
    () =>
      device === PixiSettings.DEVICE.MOBILE &&
      orientation === PixiSettings.ORIENTATION.LANDSCAPE,
    [device, orientation],
  );

  // Get responsive configuration from context
  const buttonConfig = useMemo(
    () => getResponsiveConfig(BONUS_BUY_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  // `bonus`, not `buy_bonus` — the button says BONUS. Both keys live in the
  // shared package; the copy owner picked this one, 2026-09-21.
  const displayText = useMemo(() => t("bonus").toUpperCase(), [t]);

  // Calculate ideal font size based on button size — snap to even for clean rasterization
  const dpr = window.devicePixelRatio || 1;
  const idealFontSize = useMemo(() => {
    const buttonSize = Math.min(buttonConfig.width, buttonConfig.height);
    return Math.round((buttonSize * 0.22) / 2) * 2;
  }, [buttonConfig.width, buttonConfig.height]);

  // Calculate max width for text (with padding)
  const maxTextWidth = useMemo(() => {
    return buttonConfig.width * 0.8;
  }, [buttonConfig.width]);

  // Auto-scaling font size calculation (checks both width AND height)
  const maxTextHeight = useMemo(
    () => buttonConfig.height * 0.7,
    [buttonConfig.height],
  );

  const finalFontSize = useMemo(() => {
    let fontSize = idealFontSize;

    const measure = (size) => {
      const s = new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: size,
        fontWeight: "600",
        align: "center",
        wordWrap: true,
        wordWrapWidth: maxTextWidth,
        breakWords: false,
      });
      return TextMetrics.measureText(displayText, s);
    };

    // Width pass
    let metrics = measure(fontSize);
    if (metrics.width > maxTextWidth) {
      fontSize =
        Math.round((fontSize * (maxTextWidth / metrics.width)) / 2) * 2;
    }

    // Height pass — shrink until wrapped text fits in button circle
    metrics = measure(fontSize);
    if (metrics.height > maxTextHeight) {
      fontSize =
        Math.round((fontSize * (maxTextHeight / metrics.height)) / 2) * 2;
    }

    return fontSize;
  }, [displayText, idealFontSize, maxTextWidth, maxTextHeight]);

  const textStyle = useMemo(() => {
    return new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: finalFontSize,
      fontWeight: "700",
      fill: 0x000000,
      align: "center",
      wordWrap: true,
      wordWrapWidth: maxTextWidth,
      breakWords: false,
      resolution: dpr,
    });
  }, [finalFontSize, maxTextWidth, dpr]);

  // Calculate responsive button position based on device and orientation
  const buttonPosition = useMemo(() => {
    if (isMobileSmall) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: dimensions.width - buttonConfig.width / 1.575,
          y:
            -toolbar.visualHeight -
            buttonConfig.height / 2 -
            buttonConfig.height * 0.1 -
            buttonConfig.height * (buttonConfig.stackGap ?? 1.3),
        };
      } else {
        return {
          x: buttonConfig.width * 0.6,
          y: -buttonConfig.height * 0.6,
        };
      }
    } else if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: dimensions.width - buttonConfig.width / 1.7,
          y:
            -toolbar.visualHeight -
            buttonConfig.height / 2 -
            buttonConfig.height * 0.1 -
            buttonConfig.height * 1.5,
        };
      } else {
        return {
          x: buttonConfig.width * 0.55,
          y: -buttonConfig.height * 0.65,
        };
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: dimensions.width - buttonConfig.width * 0.86,
          y: -dimensions.height / 3.35 + buttonConfig.height / 2,
        };
      } else {
        return {
          x: buttonConfig.width * 1.4,
          y: -buttonConfig.height * 1.2,
        };
      }
    } else {
      return {
        x: toolbar.width - buttonConfig.width,
        y: toolbar.height / 2 - buttonConfig.height * 1.875,
      };
    }
  }, [
    buttonConfig.stackGap,
    device,
    orientation,
    isMobileSmall,
    dimensions.width,
    dimensions.height,
    buttonConfig.width,
    buttonConfig.height,
    toolbar?.width,
    toolbar?.height,
    toolbar?.visualHeight,
  ]);

  // Disabled state — prevent interaction during spins, animations, autoplay, free rounds, or features
  const isDisabled = useMemo(() => {
    return (
      isSpinning ||
      criticalAnimationPlaying ||
      autoPlayActive ||
      anyFeatureTriggeredOrActive ||
      freeRoundsActive ||
      replaySessionActive
    );
  }, [
    isSpinning,
    criticalAnimationPlaying,
    autoPlayActive,
    anyFeatureTriggeredOrActive,
    freeRoundsActive,
    replaySessionActive,
  ]);

  // Shared hover/press hook (matches other secondary buttons)
  const {
    buttonRef,
    handlePointerOver,
    handlePointerOut,
    cleanupFilters,
    alpha,
    cursor,
  } = useButtonBrightness({ disabled: isDisabled });

  // ── Click handler — ONLY opens popup + plays sound ──────────────────────
  const handleClick = () => {
    if (isDisabled) return;

    dispatch(closeMenu());
    attemptFullscreenToggle(dispatch, device);

    if (isProcessingClickRef.current) return;

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    isProcessingClickRef.current = true;
    soundManager.play(SOUND_NAME.button_click);

    // Press animation on entire container (sprite + text animate together)
    if (buttonContainerRef.current) {
      triggerScalePress(buttonContainerRef.current);
    }

    dispatch(openBonusBuy());

    debounceTimeoutRef.current = setTimeout(() => {
      isProcessingClickRef.current = false;
    }, 500);
  };

  // Button radius for circular hit area
  const buttonRadius = useMemo(
    () => buttonConfig.width / 2,
    [buttonConfig.width],
  );

  // // The drop shadow, rebuilt only when the button's size changes.
  // const shadowTexture = useMemo(
  //   () => (buttonRadius > 0 ? makeButtonShadowTexture(buttonRadius, dpr) : null),
  //   [buttonRadius, dpr],
  // );
  // useEffect(
  //   () => () => shadowTexture?.destroy(true),
  //   [shadowTexture],
  // );
  // The drop shadow — utils/buttonShadow (one source for every round button).
  // const shadowTexture = useButtonShadow(buttonRadius); // ButtonShadow now

  // Button opacity — dimmed when disabled
  const buttonOpacity = alpha;

  // ── Initialize sprite dimensions on mount (mobile landscape only) ───────
  useEffect(() => {
    if (
      buttonSpriteRef.current &&
      !isInitializedRef.current &&
      isMobileLandscape
    ) {
      dimensionsRef.current.width = buttonConfig.width;
      dimensionsRef.current.height = buttonConfig.height;
      buttonSpriteRef.current.width = buttonConfig.width;
      buttonSpriteRef.current.height = buttonConfig.height;
      isInitializedRef.current = true;
    } else if (!isMobileLandscape) {
      isInitializedRef.current = true;
    }
  }, [isMobileLandscape, buttonConfig.width, buttonConfig.height]);

  // ── Menu show/hide animation (mobile landscape only) ────────────────────
  useEffect(() => {
    if (!isMobileLandscape) return;
    if (!buttonSpriteRef.current || !isInitializedRef.current) return;

    if (animationRef.current) {
      anime.remove(animationRef.current);
    }

    if (restoreTimeoutRef.current) {
      clearTimeout(restoreTimeoutRef.current);
      restoreTimeoutRef.current = null;
    }

    const isInitialRender = previousMenuStateRef.current === null;

    if (isInitialRender) {
      previousMenuStateRef.current = menuActive;
      return;
    }

    if (menuActive) {
      const currentWidth =
        buttonSpriteRef.current.width !== undefined
          ? buttonSpriteRef.current.width
          : buttonConfig.width;
      const currentHeight =
        buttonSpriteRef.current.height !== undefined
          ? buttonSpriteRef.current.height
          : buttonConfig.height;

      dimensionsRef.current.width = currentWidth;
      dimensionsRef.current.height = currentHeight;
      previousMenuStateRef.current = menuActive;

      animationRef.current = anime({
        targets: dimensionsRef.current,
        width: 0,
        height: 0,
        duration: 150,
        delay: 0,
        easing: "easeInQuad",
        update: () => {
          if (buttonSpriteRef.current) {
            buttonSpriteRef.current.width = dimensionsRef.current.width;
            buttonSpriteRef.current.height = dimensionsRef.current.height;
          }
          if (buttonTextRef.current) {
            const progress = dimensionsRef.current.width / buttonConfig.width;
            buttonTextRef.current.alpha = progress;
          }
        },
      });
    } else {
      const wasMenuPreviouslyActive = previousMenuStateRef.current === true;
      previousMenuStateRef.current = menuActive;

      if (wasMenuPreviouslyActive) {
        dimensionsRef.current.width = 0;
        dimensionsRef.current.height = 0;
        if (buttonSpriteRef.current) {
          buttonSpriteRef.current.width = 0;
          buttonSpriteRef.current.height = 0;
        }

        restoreTimeoutRef.current = setTimeout(() => {
          dimensionsRef.current.width = 0;
          dimensionsRef.current.height = 0;

          animationRef.current = anime({
            targets: dimensionsRef.current,
            width: buttonConfig.width,
            height: buttonConfig.height,
            duration: 150,
            delay: 0,
            easing: "easeOutQuad",
            update: () => {
              if (buttonSpriteRef.current) {
                buttonSpriteRef.current.width = dimensionsRef.current.width;
                buttonSpriteRef.current.height = dimensionsRef.current.height;
              }
              if (buttonTextRef.current) {
                const progress =
                  dimensionsRef.current.width / buttonConfig.width;
                buttonTextRef.current.alpha = progress;
              }
            },
          });
        }, 300);
      } else {
        // Safety reset — if menu is closed and no restore was needed,
        // ensure sprite is at full dimensions (guards against race conditions
        // where a previous restore timeout was cleared by effect cleanup)
        if (buttonSpriteRef.current) {
          buttonSpriteRef.current.width = buttonConfig.width;
          buttonSpriteRef.current.height = buttonConfig.height;
        }
        if (buttonTextRef.current) {
          buttonTextRef.current.alpha = 1;
        }
        dimensionsRef.current.width = buttonConfig.width;
        dimensionsRef.current.height = buttonConfig.height;
      }
    }

    return () => {
      if (restoreTimeoutRef.current) {
        clearTimeout(restoreTimeoutRef.current);
        restoreTimeoutRef.current = null;
      }
      if (animationRef.current) {
        anime.remove(animationRef.current);
      }
    };
  }, [menuActive, isMobileLandscape, buttonConfig.width, buttonConfig.height]);

  useEffect(() => {
    if (buttonRef.current) {
      if (isDisabled) {
        buttonRef.current.filters = [];
        buttonRef.current.alpha = 0.7;
      } else {
        buttonRef.current.alpha = 1;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDisabled]);

  // ── Cleanup on unmount ──────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      cleanupFilters();
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (animationRef.current) {
        anime.remove(animationRef.current);
      }
      if (restoreTimeoutRef.current) {
        clearTimeout(restoreTimeoutRef.current);
      }
    };
  }, [cleanupFilters]);

  if (isHidden) return null;

  return (
    <Container
      ref={buttonContainerRef}
      name="secondary_button_bonus_buy"
      x={buttonPosition.x}
      y={buttonPosition.y}
      alpha={buttonOpacity}
    >
      {/* Interactive circular area - handles all pointer events */}
      <Graphics
        name="button_bonus_buy_interactive_area"
        eventMode="static"
        cursor={cursor}
        hitArea={new PIXI.Circle(0, 0, buttonRadius)}
        onpointertap={handleClick}
        onpointerover={handlePointerOver}
        onpointerout={handlePointerOut}
        draw={(g) => {
          g.clear();
          // Nearly invisible fill (required for hit detection to work)
          g.beginFill(0x000000, 0.01);
          g.drawCircle(0, 0, buttonRadius);
          g.endFill();
        }}
      />

      {/* Its soft drop shadow, under the button (utils/buttonShadow) */}
      {/* {shadowTexture && ( */}
      {/* <Sprite */}
      {/* texture={shadowTexture} */}
      {/* name="button_bonus_buy_shadow" */}
      {/* anchor={0.5} */}
      {/* x={0} */}
      {/* y={0} */}
      {/* eventMode="none" */}
      {/* /> */}
      {/* )} */}
      <ButtonShadow target={buttonSpriteRef} texture={texture} width={buttonConfig.width} height={buttonConfig.height} />

      {/* Visual sprite (non-interactive, just for display) */}
      <Sprite
        ref={(node) => {
          buttonSpriteRef.current = node;
          buttonRef.current = node;
        }}
        texture={texture}
        name="button_bonus_buy_icon"
        anchor={0.5}
        width={buttonConfig.width}
        height={buttonConfig.height}
        roundPixels={true}
        x={0}
        y={0}
        alpha={1}
        eventMode="none"
      />
      <Text
        ref={buttonTextRef}
        text={displayText}
        style={textStyle}
        anchor={0.5}
        roundPixels={true}
        x={0}
        y={0}
        alpha={1}
        eventMode="none"
      />
    </Container>
  );
};

export default ButtonBonusBuy;
